/* ---------------------------------------------------------------------------
   Working calendars, on Settings (LC-43).

   The department's calendars — its working week, public holidays and seasonal
   non-working windows — as a panel on the Options screen, which is where
   Settings opens in both shells. Everybody may read them, because a planner
   choosing a contract's calendar needs to see what each one is; only the
   administrator (`calendar.manage`) edits them, decision 5 of LIFECYCLE.md §5.
   --------------------------------------------------------------------------- */

import { h, ICONS } from '../core/dom.ts';
import { actions, can, type State } from '../core/store.ts';
import { panel, panelButton } from '../components/panel.ts';
import { button, emptyState, field, tag } from '../components/widgets.ts';
import { prettyDate } from '../data/derive.ts';
import { WEEKDAY_NAMES, weekLabel, type WorkCalendar } from '../data/calendar.ts';

export function calendarsPanel(s: Readonly<State>): HTMLElement {
  const mayManage = can(s, 'calendar.manage');
  const e = s.calendarEdit;
  return panel(
    {
      title: 'Working calendars',
      meta: String(s.calendars.length),
      actions: mayManage && e === null
        ? [
            panelButton({
              icon: ICONS.plus,
              text: 'New calendar',
              title: 'Add a working calendar for this department',
              onclick: () => actions.editCalendar(null),
            }),
          ]
        : [],
    },
    s.calendarNote ? h('div', { class: 'dlg__note' }, h('span', { text: s.calendarNote })) : null,
    e ? editor(s) : null,
    s.calendars.length === 0
      ? emptyState('No working calendars have been read for this department.')
      : h('div', { class: 'cal__list' }, ...s.calendars.map((c) => calendarRow(c, mayManage && e === null))),
  );
}

function calendarRow(c: WorkCalendar, mayEdit: boolean): HTMLElement {
  return h(
    'div',
    { class: 'cal__row', 'data-calendar': c.id },
    h(
      'div',
      { class: 'cal__head' },
      h('strong', { text: c.name }),
      tag(weekLabel(c.week), 2),
      c.provisional ? tag('provisional', 3, 'Seeded, and not yet confirmed by the department') : null,
      h('span', { class: 'spacer' }),
      mayEdit ? button('Edit', { sm: true, onClick: () => actions.editCalendar(c.id) }) : null,
    ),
    h(
      'div',
      { class: 'muted' },
      `${c.holidays.length} holidays · ${c.windows.length} seasonal windows`,
      c.updatedAt ? ` · saved ${prettyDate(c.updatedAt.slice(0, 10))}${c.updatedBy ? ` by ${c.updatedBy}` : ''}` : '',
    ),
    ...c.windows.map((w) =>
      h('div', { class: 'muted', text: `${w.name}: ${prettyDate(w.from)} → ${prettyDate(w.to)}` }),
    ),
  );
}

function editor(s: Readonly<State>): HTMLElement {
  const e = s.calendarEdit!;
  return h(
    'form',
    {
      class: 'pform',
      'data-form': 'calendar',
      onsubmit: (ev: Event) => {
        ev.preventDefault();
        void actions.saveCalendar();
      },
    },
    field(
      'Name',
      h('input', {
        id: 'cal-name',
        class: 'input',
        value: e.name,
        oninput: (ev: Event) => actions.setCalendarField('name', (ev.target as HTMLInputElement).value),
      }),
      'cal-name',
    ),
    field(
      'Working week',
      h(
        'div',
        { class: 'cal__week', role: 'group', 'aria-label': 'Working week' },
        ...WEEKDAY_NAMES.map((name, i) =>
          h('button', {
            type: 'button',
            class: ['chip', e.week[i] === '1' && 'chip--on'],
            'aria-pressed': e.week[i] === '1' ? 'true' : 'false',
            'data-day': String(i),
            text: name,
            onclick: () => actions.toggleCalendarDay(i),
          }),
        ),
      ),
    ),
    field(
      'Public holidays, one per line',
      h('textarea', {
        id: 'cal-holidays',
        class: 'input',
        rows: 4,
        placeholder: '2026-12-25 Quaid-e-Azam Day',
        value: e.holidays,
        oninput: (ev: Event) => actions.setCalendarField('holidays', (ev.target as HTMLTextAreaElement).value),
      }),
      'cal-holidays',
    ),
    field(
      'Seasonal non-working windows, one per line',
      h('textarea', {
        id: 'cal-windows',
        class: 'input',
        rows: 3,
        placeholder: '2026-07-01 2026-08-31 Monsoon',
        value: e.windows,
        oninput: (ev: Event) => actions.setCalendarField('windows', (ev.target as HTMLTextAreaElement).value),
      }),
      'cal-windows',
    ),
    h(
      'div',
      { class: 'plan__acts' },
      button('Save calendar', { primary: true, type: 'submit' }),
      button('Cancel', { onClick: () => actions.cancelCalendar() }),
    ),
  );
}
